import {Job} from "../../generated/graphql";
import {pickupStop, deliveryStop} from "../../utils/stops/StopUtils";
import {getStopAddressText} from "../../utils/General";
import {getAddressPositionWithCache} from "../../providers/RoutePlanningProvider";

export const WAREHOUSE_DETECTION_THRESHOLD = 0.35;
export const WAREHOUSE_DETECTION_MIN_JOBS_FOR_THRESHOLD = 3;
const COORD_PRECISION = 5; // ~1m precision

export interface WarehouseDetectionResult {
  startWarehouseAddress: string | null;
  endWarehouseAddress: string | null;
}

const roundCoord = (val: number): string => val.toFixed(COORD_PRECISION);

const findTopAddress = async (addresses: string[], totalJobs: number): Promise<string | null> => {
  if (totalJobs === 0 || addresses.length === 0) return null;
  const threshold = totalJobs < WAREHOUSE_DETECTION_MIN_JOBS_FOR_THRESHOLD ? 1 : WAREHOUSE_DETECTION_THRESHOLD;

  // Geocode unique addresses to lat/lng, fall back to text key.
  // Use original-case address for getAddressPositionWithCache so the cache key
  // matches what the validation step uses (avoids duplicate HTTP requests).
  const uniqueByNormalized = new Map<string, string>(); // normalized → first original
  for (const addr of addresses) {
    const key = addr.trim().toLowerCase();
    if (!uniqueByNormalized.has(key)) uniqueByNormalized.set(key, addr);
  }
  const normalizedToKey = new Map<string, string>();
  await Promise.all(
    Array.from(uniqueByNormalized.entries()).map(async ([normalized, original]) => {
      try {
        const position = await getAddressPositionWithCache(original);
        if (position) {
          normalizedToKey.set(normalized, `${roundCoord(position.latitude)},${roundCoord(position.longitude)}`);
          return;
        }
      } catch {
        // geocoding failed — fall back to text
      }
      normalizedToKey.set(normalized, normalized);
    })
  );

  // Group by resolved key (lat,lng or text fallback)
  const counts = new Map<string, {count: number; original: string}>();
  for (const addr of addresses) {
    const normalized = addr.trim().toLowerCase();
    const key = normalizedToKey.get(normalized) ?? normalized;
    const existing = counts.get(key);
    if (existing) {
      existing.count++;
    } else {
      counts.set(key, {count: 1, original: addr});
    }
  }

  let topEntry: {count: number; original: string} | null = null;
  for (const entry of counts.values()) {
    if (!topEntry || entry.count > topEntry.count) {
      topEntry = entry;
    }
  }

  if (topEntry && topEntry.count / totalJobs >= threshold) {
    return topEntry.original;
  }
  return null;
};

export const detectWarehouseLocations = async (jobs: Job[]): Promise<WarehouseDetectionResult> => {
  if (!jobs || jobs.length === 0) {
    return {startWarehouseAddress: null, endWarehouseAddress: null};
  }

  const pickupAddresses: string[] = [];
  const deliveryAddresses: string[] = [];

  for (const job of jobs) {
    const sortedStops = job.stops.slice().sort((a, b) => a.sequence - b.sequence);

    const pickup = pickupStop(sortedStops);
    if (pickup) {
      const addr = getStopAddressText({stop: pickup, hasZip: true});
      if (addr) pickupAddresses.push(addr);
    }

    const delivery = deliveryStop(sortedStops);
    if (delivery) {
      const addr = getStopAddressText({stop: delivery, hasZip: true});
      if (addr) deliveryAddresses.push(addr);
    }
  }

  const [startWarehouseAddress, endWarehouseAddress] = await Promise.all([
    findTopAddress(pickupAddresses, jobs.length),
    findTopAddress(deliveryAddresses, jobs.length)
  ]);

  return {startWarehouseAddress, endWarehouseAddress};
};
